import React, { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router";
import useMyMontepioContext from "../../components/Context/useMyMontepioContext";
import { handleChannelBack } from "../../utils/handleChannelBack";
import { MGPage } from "../../designSystem/components/MGPage";
import { FormStep } from "./FormStep";
import { ModuleStep } from "./ModuleStep";
import { QuotasModalityPlanStep } from "./QuotasModalityPlanStep";
import { PlanStep } from "./PlanStep";
import { TermsStep } from "./TermsStep";
import { TPdfViewerDocument } from "../PdfViewer/types";
import { ConfirmStep } from "./ConfirmStep";
import { SuccessStep } from "./SuccessStep";
import { QuotaInsurancePlanStep } from "./QuotaInsurancePlanStep";
import { postMobileMessage } from "../../utils/postMobileMessage";
import { TGender, TModuleOption } from "./ModuleStep/components/ModuleBottomSheet/types";
import { useGetHealthFormQuery } from "./api";
import { MGLoader } from "../../designSystem/components/MGLoader";
import { MGGenericError } from "../../designSystem/components/MGGenericError";

const PdfViewerPage = React.lazy(() =>
  import("../PdfViewer").then(({ PdfViewerPage }) => ({ default: PdfViewerPage })),
);

type THealthPageProps = RouteComponentProps & {
  dataTestId?: string;
};

export type TPaymentFrequency = "MONTHLY" | "QUARTERLY" | "SEMIANNUAL" | "ANNUAL";

export type TFormHealth = {
  gender?: TGender;
  dateOfBirth?: string;
  moduleType?: string;
  paymentFrequency?: TPaymentFrequency;
  supplementaryHealthPlan?: boolean;
  protection?: {
    paymentFrequency: TPaymentFrequency;
  };
};

type THealthStep =
  | "form"
  | "module"
  | "quotasModalityPlan"
  | "plan"
  | "quotasHealthInsurancePlan"
  | "terms"
  | "confirm"
  | "success";

export const HealthPage = ({ history, dataTestId }: THealthPageProps) => {
  const { isMobileAuth, selectedUser } = useMyMontepioContext();

  const [form, setForm] = useState<TFormHealth>({});
  const [step, setStep] = useState<THealthStep>("form");
  const [pageTitle, setPageTitle] = useState<string>("Modalidade Saúde");
  const [openPdf, setOpenPdf] = useState<TPdfViewerDocument>();
  const [readDocumentIds, setReadDocumentIds] = useState<string[]>([]);
  const [readCheckboxesIds, setReadCheckboxesIds] = useState<string[]>([]);
  const [selectedModuleGender, setSelectedModuleGender] = useState<TGender>();
  const [recommendedModule, setRecommendedModule] = useState<TModuleOption>();

  const { data, isLoading, isFetching, isError, refetch } = useGetHealthFormQuery();

  const handlePdfEnd = useCallback((pdfId: string) => {
    setReadDocumentIds((currentIds) =>
      currentIds.includes(pdfId) ? currentIds : [...currentIds, pdfId],
    );
  }, []);

  const handleCheckboxChange = useCallback((id: string) => {
    setReadCheckboxesIds((prev) =>
      prev.includes(id) ? prev.filter((currentId) => currentId !== id) : [...prev, id],
    );
  }, []);

  const handleBack = () => {
    if (step === "confirm") {
      setStep("terms");
    } else if (step === "terms" && form.supplementaryHealthPlan === true) {
      setStep("quotasHealthInsurancePlan");
    } else if (step === "terms" && form.supplementaryHealthPlan === false) {
      setStep("plan");
    } else if (step === "quotasHealthInsurancePlan") {
      setStep("plan");
    } else if (step === "plan") {
      setStep("quotasModalityPlan");
    } else if (step === "quotasModalityPlan") {
      setStep("module");
    } else if (step === "module") {
      setStep("form");
    } else {
      handleChannelBack({ isMobileAuth, onWebBack: history.goBack });
    }
  };

  useEffect(() => {
    if (step === "confirm") {
      setPageTitle("Confirmar dados");
    } else if (step === "terms") {
      setPageTitle("Termos e Condições");
    } else if (step === "plan") {
      setPageTitle("Plano Assegura a Saúde");
    } else if (step === "quotasModalityPlan") {
      setPageTitle("Quotas da Modalidade");
    } else if (step === "quotasHealthInsurancePlan") {
      setPageTitle("Quota do Plano Assegura a Saúde");
    } else {
      setPageTitle("Modalidade Saúde");
    }
  }, [step]);

  const progress = useMemo(() => {
    switch (step) {
      case "form":
        return 1;
      case "module":
        return 2;
      case "quotasModalityPlan":
        return 3;
      case "plan":
        return 4;
      case "quotasHealthInsurancePlan":
      case "terms":
        return 5;
      case "confirm":
        return 6;
    }
  }, [step]);

  useEffect(() => {
    setForm((currentForm) => ({
      ...currentForm,
      dateOfBirth: selectedUser.birthDate,
    }));
  }, [selectedUser.birthDate]);

  const onFormCompleted = (formHealth: TFormHealth) => {
    setForm((currentForm) => ({ ...currentForm, ...formHealth }));
    setSelectedModuleGender(formHealth.gender);
    setStep("module");
  };

  const onModuleCompleted = (
    formHealth: TFormHealth,
    selectedModuleGender: TGender,
    recommendedModule: TModuleOption,
  ) => {
    setForm((currentForm) => ({ ...currentForm, ...formHealth }));
    setSelectedModuleGender(selectedModuleGender);
    setRecommendedModule(recommendedModule);
    setStep("quotasModalityPlan");
  };

  const onQuotasModalityCompleted = (formHealth: TFormHealth) => {
    setForm((currentForm) => ({ ...currentForm, ...formHealth }));

    if (selectedUser.age ?? 0 > 65) setStep("terms");
    else setStep("plan");
  };

  const onQuotaInsuranceCompleted = (formHealth: TFormHealth) => {
    setForm((currentForm) => ({ ...currentForm, ...formHealth }));
    setStep("terms");
  };

  const onPlanCompleted = (formHealth: TFormHealth) => {
    setForm((currentForm) => ({ ...currentForm, ...formHealth }));

    formHealth.supplementaryHealthPlan ? setStep("quotasHealthInsurancePlan") : setStep("terms");
  };

  const onTermsCompleted = (formHealth: TFormHealth) => {
    setForm((currentForm) => ({ ...currentForm, ...formHealth }));
    setStep("confirm");
  };

  const onConfirmCompleted = (formHealth: TFormHealth) => {
    setStep("success");
  };

  const onSucces = () => {
    isMobileAuth ? postMobileMessage("FLOW_COMPLETED") : history.replace("/");
  };

  const handleOpenPdf = useCallback((document: TPdfViewerDocument) => setOpenPdf(document), []);

  if (openPdf) {
    return (
      <Suspense fallback={null}>
        <PdfViewerPage
          {...openPdf}
          dataTestId={dataTestId && `${dataTestId}-pdf-viewer`}
          onBack={() => setOpenPdf(undefined)}
        />
      </Suspense>
    );
  }

  if (isLoading || isFetching) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <MGLoader />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <MGGenericError
          actionsAtBottom={isMobileAuth}
          title="Mensagem de Erro !"
          description="Explicação adicional, próximos passos e outras opções de contexto"
          primaryActionLabel="Tentar novamente"
          secondaryActionLabel="Voltar"
          onPrimaryAction={refetch}
          onSecondaryAction={handleBack}
        />
      </div>
    );
  }

  return (
    <MGPage
      scrollToTopKey={step}
      isMobileAuth={isMobileAuth}
      dataTestId={dataTestId && `${dataTestId}-health-page`}
      toolbar={
        step === "success"
          ? undefined
          : {
              title: pageTitle,
              dataTestId: dataTestId && `${dataTestId}-health-page-toolbar`,
              isFixed: isMobileAuth,
              titleAlign: isMobileAuth ? "center" : "left",
              currentStep: progress,
              totalSteps: 6,
              progressHeight: isMobileAuth ? 4 : 8,
              onBack: handleBack,
              onClose: isMobileAuth ? () => postMobileMessage("FLOW_CANCELLED") : undefined,
            }
      }>
      {step === "form" && (
        <FormStep formHealth={form} content={data!} onContinue={onFormCompleted} />
      )}
      {step === "module" && (
        <ModuleStep
          formHealth={form}
          onOpenPdf={handleOpenPdf}
          selectedModuleGender={selectedModuleGender}
          recommendedModule={recommendedModule}
          onContinue={onModuleCompleted}
        />
      )}
      {step === "quotasModalityPlan" && (
        <QuotasModalityPlanStep
          formHealth={form}
          onContinue={onQuotasModalityCompleted}
          onBack={handleBack}
        />
      )}
      {step === "quotasHealthInsurancePlan" && (
        <QuotaInsurancePlanStep
          formHealth={form}
          onContinue={onQuotaInsuranceCompleted}
          onBack={handleBack}
        />
      )}
      {step === "plan" && (
        <PlanStep formHealth={form} onContinue={onPlanCompleted} onBack={handleBack} />
      )}
      {step === "terms" && (
        <TermsStep
          formHealth={form}
          content={data!}
          readDocumentIds={readDocumentIds}
          onContinue={onTermsCompleted}
          onOpenPdf={handleOpenPdf}
          onPdfEnd={handlePdfEnd}
          readCheckboxesIds={readCheckboxesIds}
          handleCheckboxChange={handleCheckboxChange}
        />
      )}
      {step === "confirm" && (
        <ConfirmStep formHealth={form} onContinue={onConfirmCompleted} onBack={handleBack} />
      )}
      {step === "success" && (
        <SuccessStep supplementaryHealthPlan={form.supplementaryHealthPlan} onContinue={onSucces} />
      )}
    </MGPage>
  );
};
